import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader
} from "@prismicio/editor-ui"

import type {
  SliceConfigWithSharedSlice,
  VisualSharedSlice
} from "../sliceTableData"
import { PagePreview } from "./PagePreview"
import { SlicePicker } from "./SlicePicker"
import { SmartEditor } from "./SmartEditor"
import { useSmartCopyPaste } from "./useSmartCopyPaste"

interface Props {
  open: boolean
  slices: SliceConfigWithSharedSlice[]
  onFinish: (args?: { slices: VisualSharedSlice[] }) => void
}

export function SmartCopyPasteDialog(props: Props) {
  const { open, slices, onFinish } = props

  function onOpenChange(newOpen: boolean) {
    if (!newOpen) onFinish()
  }

  const { editor, fitSelectionIntoSlice, items, retry } = useSmartCopyPaste({
    slices,
    active: open
  })

  const addSlicesDisabled = Object.values(items).every(
    (item) => item.status !== "done"
  )

  const addSlices = () => {
    const slices = Object.values(items).reduce<VisualSharedSlice[]>(
      (slices, item) =>
        item.status === "done" ? [...slices, item.slice] : slices,
      []
    )
    onFinish({ slices })
  }

  const slicePickerDisabled =
    !editor ||
    editor.isDestroyed ||
    editor.isEmpty ||
    editor.state.selection.empty

  return (
    <Dialog
      size={{
        // Full width minus the 24px padding on the left and right. Max width is 2000px on large screens.
        width: "min(calc(100% - 48px), 2000px)",
        // Full height minus the 15px padding on the top and bottom.
        height: "calc(100% - 30px)"
      }}
      position="center"
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogHeader icon="contentCopy" title="AI smart copy paste" />
      <DialogContent>
        <Box
          backgroundColor="grey2"
          height="100%"
          padding={{ inline: 24 }}
          display="grid"
          gridTemplateColumns="6fr 5fr 6fr"
          gap={24}
        >
          <SmartEditor editor={editor} />
          <SlicePicker
            disabled={slicePickerDisabled}
            slices={slices}
            onSelectSlice={fitSelectionIntoSlice}
          />
          <PagePreview items={items} onRetry={retry} />
        </Box>
      </DialogContent>
      <DialogActions>
        <DialogCancelButton />
        <DialogActionButton onClick={addSlices} disabled={addSlicesDisabled}>
          Add slices
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}
